import api from './api.service';

export type QmsAgingBucket = '0-7' | '8-14' | '15-30' | '30+';

export type QmsAgingMap = Record<QmsAgingBucket, number>;

export type NcrCapaQueueMode = 'open_ncr' | 'overdue_capa' | 'overdue_inspection';

export interface PendingIIRRow {
  id: string;
  iir_number: string;
  status: string;
  work_order_id: string;
  work_order_number?: string;
  requested_date: string;
  link_path: string;
}

export interface PendingStageInspectionRow {
  id: string;
  inspection_number: string;
  status: string;
  work_order_id: string;
  work_order_number?: string;
  link_path: string;
}

export interface PendingFIRRow {
  id: string;
  fir_number: string;
  status: string;
  work_order_id: string;
  work_order_number?: string;
  requested_date: string;
  link_path: string;
}

export interface PendingIRNApprovalRow {
  id: string;
  irn_number: string;
  status: string;
  work_order_number?: string;
  link_path: string;
}

export interface PendingQmsFirReportRow {
  id: string;
  fir_number: string;
  approval_status: string;
  work_order_number?: string;
  inspection_date?: string;
  link_path: string;
}

export interface PendingPlanItemRow {
  id: string;
  quality_plan_id: string;
  plan_number?: string;
  description: string;
  inspection_type: string;
  scheduled_date?: string;
  link_path: string;
}

export interface PlanUnderReviewRow {
  id: string;
  plan_number: string;
  status: string;
  link_path: string;
}

export interface InspectionQueueRow {
  id: string;
  inspection_number: string;
  status: string;
  scheduled_date?: string;
  overdue_days?: number;
  aging_bucket?: QmsAgingBucket;
  link_path: string;
}

export interface OpenNCRRow {
  id: string;
  ncr_number: string;
  severity: string;
  status: string;
  identified_date: string;
  aging_days: number;
  aging_bucket: QmsAgingBucket;
  link_path: string;
}

export interface OverdueCAPARow {
  id: string;
  capa_number: string;
  status: string;
  target_completion_date: string;
  overdue_days: number;
  aging_bucket: QmsAgingBucket;
  link_path: string;
}

export interface QualityDashboardActionSummary {
  pending_iirs: PendingIIRRow[];
  pending_iir_count: number;
  pending_stage_inspections: PendingStageInspectionRow[];
  pending_stage_count: number;
  failed_stage_inspections: PendingStageInspectionRow[];
  failed_stage_count: number;
  overdue_inspections: InspectionQueueRow[];
  overdue_inspection_count: number;
  pending_firs: PendingFIRRow[];
  pending_fir_count: number;
  pending_irn_approvals: PendingIRNApprovalRow[];
  pending_irn_count: number;
  pending_qms_fir_reports: PendingQmsFirReportRow[];
  pending_qms_fir_count: number;
  pending_plan_items: PendingPlanItemRow[];
  pending_plan_item_count: number;
  plan_items_due_soon_count: number;
  plans_under_review: PlanUnderReviewRow[];
  plans_under_review_count: number;
  inspections_in_flight: InspectionQueueRow[];
  inspections_in_flight_count: number;
  open_ncrs: OpenNCRRow[];
  open_ncr_count: number;
  critical_ncr_count: number;
  open_ncr_aging: QmsAgingMap;
  overdue_capas: OverdueCAPARow[];
  overdue_capa_count: number;
  overdue_capa_aging: QmsAgingMap;
  open_capa_count: number;
  draft_plan_count: number;
  scheduled_audit_count: number;
  inspection_pass_rate_30d: number | null;
  wo_gates_waiting_count: number;
  approvals_waiting_count: number;
}

export interface NcrCapaQueueResult {
  mode: NcrCapaQueueMode;
  total: number;
  page: number;
  pageSize: number;
  aging: QmsAgingMap;
  rows: Array<OpenNCRRow | OverdueCAPARow | InspectionQueueRow>;
}

function emptyAging(): QmsAgingMap {
  return { '0-7': 0, '8-14': 0, '15-30': 0, '30+': 0 };
}

function normalizeAging(raw: unknown): QmsAgingMap {
  const base = emptyAging();
  if (!raw || typeof raw !== 'object') return base;
  const src = raw as Record<string, number>;
  for (const key of Object.keys(base) as QmsAgingBucket[]) {
    base[key] = Number(src[key] ?? 0);
  }
  return base;
}

export function emptyQualityDashboardSummary(): QualityDashboardActionSummary {
  return {
    pending_iirs: [],
    pending_iir_count: 0,
    pending_stage_inspections: [],
    pending_stage_count: 0,
    failed_stage_inspections: [],
    failed_stage_count: 0,
    overdue_inspections: [],
    overdue_inspection_count: 0,
    pending_firs: [],
    pending_fir_count: 0,
    pending_irn_approvals: [],
    pending_irn_count: 0,
    pending_qms_fir_reports: [],
    pending_qms_fir_count: 0,
    pending_plan_items: [],
    pending_plan_item_count: 0,
    plan_items_due_soon_count: 0,
    plans_under_review: [],
    plans_under_review_count: 0,
    inspections_in_flight: [],
    inspections_in_flight_count: 0,
    open_ncrs: [],
    open_ncr_count: 0,
    critical_ncr_count: 0,
    open_ncr_aging: emptyAging(),
    overdue_capas: [],
    overdue_capa_count: 0,
    overdue_capa_aging: emptyAging(),
    open_capa_count: 0,
    draft_plan_count: 0,
    scheduled_audit_count: 0,
    inspection_pass_rate_30d: null,
    wo_gates_waiting_count: 0,
    approvals_waiting_count: 0,
  };
}

export async function getQualityDashboardActionSummary(params?: {
  as_on_date?: string;
}): Promise<QualityDashboardActionSummary> {
  const res = await api.get<{ data?: QualityDashboardActionSummary }>('/quality/dashboard/action-summary', {
    params: params?.as_on_date ? { as_on_date: params.as_on_date } : undefined,
  });
  const data = (res as any)?.data?.data ?? (res as any)?.data ?? res;
  if (!data || typeof data !== 'object') {
    return emptyQualityDashboardSummary();
  }
  return {
    pending_iirs: Array.isArray(data.pending_iirs) ? data.pending_iirs : [],
    pending_iir_count: Number(data.pending_iir_count ?? data.pending_iirs?.length ?? 0),
    pending_stage_inspections: Array.isArray(data.pending_stage_inspections)
      ? data.pending_stage_inspections
      : [],
    pending_stage_count: Number(data.pending_stage_count ?? data.pending_stage_inspections?.length ?? 0),
    failed_stage_inspections: Array.isArray(data.failed_stage_inspections)
      ? data.failed_stage_inspections
      : [],
    failed_stage_count: Number(data.failed_stage_count ?? data.failed_stage_inspections?.length ?? 0),
    overdue_inspections: Array.isArray(data.overdue_inspections) ? data.overdue_inspections : [],
    overdue_inspection_count: Number(
      data.overdue_inspection_count ?? data.overdue_inspections?.length ?? 0
    ),
    pending_firs: Array.isArray(data.pending_firs) ? data.pending_firs : [],
    pending_fir_count: Number(data.pending_fir_count ?? data.pending_firs?.length ?? 0),
    pending_irn_approvals: Array.isArray(data.pending_irn_approvals) ? data.pending_irn_approvals : [],
    pending_irn_count: Number(data.pending_irn_count ?? data.pending_irn_approvals?.length ?? 0),
    pending_qms_fir_reports: Array.isArray(data.pending_qms_fir_reports)
      ? data.pending_qms_fir_reports
      : [],
    pending_qms_fir_count: Number(
      data.pending_qms_fir_count ?? data.pending_qms_fir_reports?.length ?? 0
    ),
    pending_plan_items: Array.isArray(data.pending_plan_items) ? data.pending_plan_items : [],
    pending_plan_item_count: Number(
      data.pending_plan_item_count ?? data.pending_plan_items?.length ?? 0
    ),
    plan_items_due_soon_count: Number(data.plan_items_due_soon_count ?? 0),
    plans_under_review: Array.isArray(data.plans_under_review) ? data.plans_under_review : [],
    plans_under_review_count: Number(
      data.plans_under_review_count ?? data.plans_under_review?.length ?? 0
    ),
    inspections_in_flight: Array.isArray(data.inspections_in_flight) ? data.inspections_in_flight : [],
    inspections_in_flight_count: Number(
      data.inspections_in_flight_count ?? data.inspections_in_flight?.length ?? 0
    ),
    open_ncrs: Array.isArray(data.open_ncrs) ? data.open_ncrs : [],
    open_ncr_count: Number(data.open_ncr_count ?? 0),
    critical_ncr_count: Number(data.critical_ncr_count ?? 0),
    open_ncr_aging: normalizeAging(data.open_ncr_aging),
    overdue_capas: Array.isArray(data.overdue_capas) ? data.overdue_capas : [],
    overdue_capa_count: Number(data.overdue_capa_count ?? data.overdue_capas?.length ?? 0),
    overdue_capa_aging: normalizeAging(data.overdue_capa_aging),
    open_capa_count: Number(data.open_capa_count ?? 0),
    draft_plan_count: Number(data.draft_plan_count ?? 0),
    scheduled_audit_count: Number(data.scheduled_audit_count ?? 0),
    inspection_pass_rate_30d:
      data.inspection_pass_rate_30d == null ? null : Number(data.inspection_pass_rate_30d),
    wo_gates_waiting_count: Number(
      data.wo_gates_waiting_count ??
        (Number(data.pending_iir_count ?? 0) +
          Number(data.pending_stage_count ?? 0) +
          Number(data.pending_fir_count ?? 0))
    ),
    approvals_waiting_count: Number(
      data.approvals_waiting_count ??
        (Number(data.pending_irn_count ?? 0) +
          Number(data.pending_qms_fir_count ?? 0) +
          Number(data.plans_under_review_count ?? 0))
    ),
  };
}

const QUEUE_MODES: NcrCapaQueueMode[] = ['open_ncr', 'overdue_capa', 'overdue_inspection'];

export async function getQualityNcrCapaQueue(params?: {
  mode?: NcrCapaQueueMode;
  page?: number;
  pageSize?: number;
  aging_bucket?: QmsAgingBucket;
  search?: string;
  as_on_date?: string;
}): Promise<NcrCapaQueueResult> {
  const res = await api.get<{ data?: NcrCapaQueueResult }>('/quality/dashboard/ncr-capa-queue', {
    params,
  });
  const data = (res as any)?.data?.data ?? (res as any)?.data ?? res;
  return {
    mode: (QUEUE_MODES.includes(data?.mode) ? data.mode : 'open_ncr') as NcrCapaQueueMode,
    total: Number(data?.total ?? 0),
    page: Number(data?.page ?? 1),
    pageSize: Number(data?.pageSize ?? 20),
    aging: normalizeAging(data?.aging),
    rows: Array.isArray(data?.rows) ? data.rows : [],
  };
}
